import {
  Alert,
  AlertDescription,
  Box,
  Stack,
  Heading,
  Button,
  Flex,
  Menu,
  MenuButton,
  Icon,
  useDisclosure,
  Collapse,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';

import { LoginProvider } from './components/login-manager';
import DividerBox from '@/ui/DividerBox';

import { Wrapper } from '@/components/login/Wrapper';
import { GoogleLogin } from './components/GoogleLogin';
import { useAuth, useRedirectData } from '@/hooks';
import { Login as UserLogin } from './components/Login';
import { KText } from '@/ui-library/components/text';
import {
  isVerificationRequired,
  getErrorsCode,
  getErrorMessage,
} from './utils/login-error';
import { MicrosoftLogin } from './components/MicrosoftLogin';
import { ChevronDown, Key01, Mail01 } from '@kula-ai/icons-core';
import { http } from '@/lib/http';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@/design-system/loader';
import { iconMappings } from '@/constants/samlIconMappings';

export const LoginRevamp = () => {
  const redirectData = useRedirectData();
  const { getAuth, login, isLoggingIn, bootstrap } = useAuth();
  const [otpRequired, setOtp] = useState(false);
  const [email, setEmail] = useState(null);
  const [loginInitiated, setLogin] = useState(false);
  // `useRedirectData` returns the OAuth callback errors on the first render only, and this
  // page re-renders once auth options load, so keep them in state to survive that render.
  const [errors, setErrors] = useState<string[]>(
    () => redirectData?.errors ?? []
  );
  const [showUserLogin, setShowUserLogin] = useState(false);
  const { data: authOptions, isLoading: isAuthOptionsLoading } = useQuery({
    queryKey: ['authOptions'],
    queryFn: () => http.get('/account_settings/auth_options'),
  });
  const { data: ssoOptions, isLoading: isSsoLoading } = useQuery({
    queryKey: ['ssoOptions'],
    queryFn: async () => {
      const response = await http.get('/saml/sso_logins');
      return response?.data;
    },
  });

  const invalidDomainText = useMemo(
    () =>
      authOptions === 'invalid domain'
        ? {
            heading: 'This account does not exist',
            subheading:
              'Double-check your subdomain (acme.kula.ai), or contact support@kula.ai for help',
          }
        : { heading: null, subheading: null },
    [authOptions]
  );
  const popoverActions = useDisclosure();
  const isLoading = isAuthOptionsLoading || isSsoLoading;

  const SSO_LOGIN_ENABLED =
    authOptions?.data?.auth_options?.sso_login_enabled &&
    ssoOptions?.sso_providers?.length > 0 &&
    !isLoading;
  const GOOGLE_LOGIN_ENABLED =
    authOptions?.data?.auth_options?.google_login_enabled && !isLoading;
  const MICROSOFT_LOGIN_ENABLED =
    authOptions?.data?.auth_options?.microsoft_login_enabled && !isLoading;
  const DEFAULT_LOGIN_ENABLED =
    authOptions?.data?.auth_options?.default_login_enabled && !isLoading;

  const showFallbackLogin = !authOptions && !isLoading;

  const loginOptionsCount = [
    SSO_LOGIN_ENABLED,
    GOOGLE_LOGIN_ENABLED,
    MICROSOFT_LOGIN_ENABLED,
    DEFAULT_LOGIN_ENABLED,
  ].filter(Boolean).length;

  useEffect(() => {
    if (!loginInitiated || isLoggingIn) return;
    bootstrap();
  }, [loginInitiated, isLoggingIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClickLogin = async ({ email, password, otpRequired, otp }) => {
    try {
      if (!email || !password) return;
      const payload = {};
      payload['email'] = email;
      payload['password'] = password;
      if (otpRequired) {
        payload['otp'] = otp;
      }
      const res = await login(payload);

      //add errors based on the status
      if (res?.status === 404 || res === 'invalid domain') {
        // todo - confirm copy with product
        setErrors([
          'Invalid domain – please check your domain URL and try again.',
        ]);
        return;
      }

      if (getAuth()) {
        setLogin(true);
      }
      if (!res?.errors?.length) return;
      // Note - This is a bridge which converts error string to error code.
      // Once API is released with Error code, remove this bridge.
      const errorCode = getErrorsCode(res?.errors);
      setErrors(errorCode);
      if (errorCode?.length > 0 && isVerificationRequired(errorCode)) {
        setEmail(email);
        setOtp(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <LoginProvider value={{ otpRequired, email }}>
      <Wrapper
        title="Recruitment happens here"
        desc="A recruitment platform. For recruiters. By recruiters."
      >
        {/* One tree either way. Returning a different root while loading
            remounted `Wrapper`, and its `useBreakpointValue` reports
            `undefined` on a first render before it resolves — so `showImage`
            went false for a beat and the hero image dropped out and came back.
            Branching here instead keeps the chrome, and the image, mounted:
            the largest element on the page paints once, before the two auth
            calls land, and only the buttons wait on them. */}
        {isLoading ? (
          <Flex alignItems="center" justifyContent="center" py="40px">
            <Loader />
          </Flex>
        ) : (
          <>
            <Stack align={'center'} my="20px">
              {!invalidDomainText?.heading && (
                <Heading fontSize="32px" color="secondary-900">
                  Log in
                </Heading>
              )}
              {invalidDomainText?.heading && (
                <KText size="lg-medium" color="secondary-900">
                  {invalidDomainText?.heading}
                </KText>
              )}
              {invalidDomainText?.subheading && (
                <KText
                  size="sm-medium"
                  color="secondary-500"
                  mt="8px"
                  textAlign="center"
                >
                  {invalidDomainText?.subheading}
                </KText>
              )}
            </Stack>
            <Box
              sx={{
                '.chakra-collapse': {
                  height: 'fit-content !important',
                },
              }}
            >
              <Stack spacing={4}>
                {errors?.map?.((error, idx) => (
                  <Alert
                    key={idx}
                    status="error"
                    borderRadius="6px"
                    bg="var(--kula-dark-secondary-50, #FDEDEF)"
                  >
                    <AlertDescription
                      color="#EB4A27"
                      fontSize="14px"
                      fontWeight="semiBold"
                      data-test-id="login-error-toast"
                    >
                      {getErrorMessage(error)}
                    </AlertDescription>
                  </Alert>
                ))}
                {(GOOGLE_LOGIN_ENABLED || showFallbackLogin) && <GoogleLogin />}
                {(MICROSOFT_LOGIN_ENABLED || showFallbackLogin) && (
                  <MicrosoftLogin />
                )}
                {SSO_LOGIN_ENABLED &&
                  ssoOptions?.sso_providers?.length === 1 && (
                    <form method="get" action={`/api/v1/saml/redirect_to_sso`}>
                      <input
                        type="hidden"
                        name="integration_type"
                        value={ssoOptions?.sso_providers?.[0]?.integration_type}
                      />
                      <Button
                        type="submit"
                        variant="quaternary"
                        width="full"
                        height="40px"
                        leftIcon={
                          <Icon
                            as={
                              iconMappings.find(
                                (icon) =>
                                  icon.integration_type ===
                                  ssoOptions?.sso_providers?.[0]
                                    ?.integration_type
                              )?.icon || Key01
                            }
                            w="20px"
                            h="20px"
                          />
                        }
                      >
                        Sign in with{' '}
                        {ssoOptions?.sso_providers?.[0]?.name
                          ? ssoOptions?.sso_providers?.[0]?.name
                          : ssoOptions?.sso_providers?.[0]?.integration_type ===
                              'rippling/rippling_api'
                            ? 'Rippling'
                            : ''}
                      </Button>
                    </form>
                  )}

                {SSO_LOGIN_ENABLED && ssoOptions?.sso_providers?.length > 1 && (
                  <Flex
                    justifyContent="center"
                    border={'1px solid'}
                    borderRadius="6px"
                    borderColor={'border-color'}
                    bg="base-white"
                    position="relative"
                    w="100%"
                  >
                    <Flex
                      alignItems="center"
                      py="8px"
                      px="16px"
                      gap="6px"
                      w="100%"
                      justifyContent="center"
                      borderLeftRadius="6px"
                      background={'transparent'}
                      cursor={'pointer'}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        popoverActions?.onToggle?.();
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        popoverActions?.onToggle?.();
                      }}
                      _hover={{
                        bg: 'secondary-25',
                        borderRadius: '6px',
                      }}
                    >
                      <Key01 size="1em" className="kula-icon" />
                      <KText size="sm-medium">Sign in with SSO</KText>
                      <ChevronDown size="1em" className="kula-icon" />
                    </Flex>

                    <Menu
                      isOpen={popoverActions?.isOpen}
                      onClose={() => popoverActions?.onClose()}
                      closeOnBlur={true}
                      closeOnSelect={true}
                      gutter={4}
                    >
                      <MenuButton
                        borderRightRadius="4px"
                        position="absolute"
                        opacity="0"
                        height="0"
                        width="0"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          popoverActions?.onToggle?.();
                        }}
                      ></MenuButton>
                      <Box
                        position="absolute"
                        top="calc(100% + 4px)"
                        left="0"
                        width="100%"
                        zIndex="dropdown"
                        display={popoverActions.isOpen ? 'block' : 'none'}
                        maxHeight="240px"
                        overflowY="auto"
                      >
                        <Box
                          p="0"
                          border="1px solid"
                          borderRadius="6px"
                          borderColor="border-color"
                          bg="white"
                          width="100%"
                          boxShadow="md"
                        >
                          {ssoOptions?.sso_providers?.map((option, index) => (
                            <Flex
                              key={option?.integration_type}
                              gap="8px"
                              py="10px"
                              px="16px"
                              cursor="pointer"
                              onClick={() => {
                                // Create and submit a form dynamically
                                const form = document.createElement('form');
                                form.method = 'get';
                                form.action = `/api/v1/saml/redirect_to_sso`;

                                const input = document.createElement('input');
                                input.type = 'hidden';
                                input.name = 'integration_type';
                                input.value = option?.integration_type;

                                form.appendChild(input);
                                document.body.appendChild(form);
                                form.submit();

                                popoverActions.onClose();
                              }}
                              onTouchEnd={() => {
                                // Create and submit a form dynamically
                                const form = document.createElement('form');
                                form.method = 'get';
                                form.action = `/api/v1/saml/redirect_to_sso`;

                                const input = document.createElement('input');
                                input.type = 'hidden';
                                input.name = 'integration_type';
                                input.value = option?.integration_type;

                                form.appendChild(input);
                                document.body.appendChild(form);
                                form.submit();

                                popoverActions.onClose();
                              }}
                              width="100%"
                              alignItems="center"
                              justifyContent="flex-start"
                              borderBottom={
                                index !== ssoOptions?.sso_providers?.length - 1
                                  ? '1px solid'
                                  : 'none'
                              }
                              borderColor="border-color"
                              _hover={{
                                bg: 'secondary-25',
                              }}
                            >
                              <Icon
                                as={
                                  iconMappings.find(
                                    (icon) =>
                                      icon.integration_type ===
                                      option?.integration_type
                                  )?.icon || Key01
                                }
                                w="20px"
                                h="20px"
                              />
                              <KText size="sm-medium">
                                {option.name
                                  ? `Sign in with ${option.name}`
                                  : option?.integration_type ===
                                      'rippling/rippling_api'
                                    ? 'Sign in with Rippling'
                                    : ''}
                              </KText>
                            </Flex>
                          ))}
                        </Box>
                      </Box>
                    </Menu>
                  </Flex>
                )}
              </Stack>
              {DEFAULT_LOGIN_ENABLED &&
                SSO_LOGIN_ENABLED &&
                loginOptionsCount > 1 && (
                  <>
                    <Collapse in={showUserLogin} animateOpacity>
                      <Box mt="16px">
                        <DividerBox>
                          <KText>Or, sign in with your email</KText>
                        </DividerBox>
                        <UserLogin onLogin={onClickLogin} />
                      </Box>
                    </Collapse>

                    {!showUserLogin && (
                      <Button
                        type="button"
                        variant="quaternary"
                        height="40px"
                        leftIcon={<Mail01 size="1em" />}
                        width="full"
                        mt="16px"
                        onClick={() => setShowUserLogin(true)}
                      >
                        Sign in with your email
                      </Button>
                    )}
                  </>
                )}
              {((DEFAULT_LOGIN_ENABLED &&
                !SSO_LOGIN_ENABLED &&
                loginOptionsCount > 1) ||
                showFallbackLogin) && (
                <>
                  <Box mt="16px">
                    <DividerBox>
                      <KText>Or, sign in with your email</KText>
                    </DividerBox>
                    <UserLogin onLogin={onClickLogin} />
                  </Box>
                </>
              )}
              {DEFAULT_LOGIN_ENABLED && loginOptionsCount === 1 && (
                <Box mt="16px">
                  <UserLogin onLogin={onClickLogin} />
                </Box>
              )}
            </Box>
          </>
        )}
      </Wrapper>
    </LoginProvider>
  );
};
